import {
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Tag,
} from 'antd';
import {EditTwoTone, InfoCircleOutlined, PlusOutlined} from '@ant-design/icons';

import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../common/hooks';
import {
  batteryCategoryList,
  addBatteryCategory,
  updateBatteryCategory,
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
  batteryCategoryStatusFilter,
} from './batteryCategorySlice';
import type {
  ADD_BATTERY_CATEGORY,
  UPDATE_BATTERY_CATEGORY,
  BATTERY_CATEGORY,
} from './batteryCategory.type';
import {formatDate} from '../../../../utils/formatDate';
import checkFormUpdate from '../../../../utils/checkFormUpdate';
import {sortbyFilter, statusFilter, statusFilterOptions} from '../../../../common/constants';

const BatteryCategory = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.batteryCategory);
  const config = useAppSelector((state) => state.config.config);
  const [isBatteryCategoryModalOpen, setIsBatteryCategoryModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isUpdateModal, setIsUpdateModal] = useState(false);
  const [batteryCategoryForm] = Form.useForm();
  const {Option} = Select;

  const [modalData, setModalData] = useState<BATTERY_CATEGORY>({} as BATTERY_CATEGORY);

  const handleUpdateBatteryCategory = (values: UPDATE_BATTERY_CATEGORY) => {
    const payload = checkFormUpdate(values, modalData);

    if (Object.keys(payload).length > 0) {
      dispatch(updateBatteryCategory({payload: payload, category_id: modalData.id}))
        .unwrap()
        .then(() => {
          setIsBatteryCategoryModalOpen(false);
          dispatch(
            batteryCategoryList({
              page_size: state.batteryCategories.page_size,
              page_number: state.batteryCategories.page_number,
              sort_by: state.batteryCategories.sort_by,
              ascending: state.batteryCategories.ascending,
              search: state.batteryCategories.search,
            }),
          );
        });
      //.catch((error) => {});
    } else {
      message.warning('No fields are updated');
    }
  };

  const handleAddBatteryCategory = (values: ADD_BATTERY_CATEGORY) => {
    dispatch(addBatteryCategory(values))
      .unwrap()
      .then(() => {
        batteryCategoryForm.resetFields();
        setIsBatteryCategoryModalOpen(false);
        dispatch(
          batteryCategoryList({
            page_size: state.batteryCategories.page_size,
            page_number: state.batteryCategories.page_number,
            sort_by: state.batteryCategories.sort_by,
            ascending: state.batteryCategories.ascending,
            search: state.batteryCategories.search,
          }),
        );
      });
  };

  useEffect(() => {
    dispatch(
      batteryCategoryList({
        page_size: state.batteryCategories.page_size,
        page_number: state.batteryCategories.page_number,
        sort_by: state.batteryCategories.sort_by,
        ascending: state.batteryCategories.ascending,
        search: state.batteryCategories.search,
        is_active: state.batteryCategories.is_active,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.batteryCategories.page_number,
    state.batteryCategories.page_size,
    state.batteryCategories.search,
    state.batteryCategories.is_active,
    state.batteryCategories.sort_by,
    state.batteryCategories.ascending,
  ]);

  const columns: ColumnsType<BATTERY_CATEGORY> = [
    {
      title: 'S. No.',
      width: 70,
      render: (test, record, index) => {
        return (
          <>
            {(state.batteryCategories.page_number - 1) * state.batteryCategories.page_size +
              index +
              1}
          </>
        );
      },
    },
    {
      title: ' Name',
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: ' Chemistry',
      dataIndex: 'cell_chemistry',
    },
    {
      title: 'Port',
      dataIndex: 'conn_port',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      render: (_, record) => {
        return record.is_active === true ? (
          <Tag color='green'>Active</Tag>
        ) : (
          <Tag color='red'>Inactive</Tag>
        );
      },
    },

    {
      title: 'Actions',
      key: 'operation',
      render: (_, record) => {
        return (
          <>
            <InfoCircleOutlined
              style={{cursor: 'pointer', fontSize: '16px', color: 'green', marginInline: '10px'}}
              onClick={() => {
                setModalData(record);
                setIsInfoModalOpen(true);
              }}
            />
            {config.permissions.update_batterycategory ? (
              <EditTwoTone
                style={{cursor: 'pointer', fontSize: '16px', color: 'blue', marginInline: '10px'}}
                onClick={() => {
                  setModalData(record);
                  setIsUpdateModal(true);
                  batteryCategoryForm.setFieldsValue({
                    name: record.name,
                    peak_current: record.peak_current,
                    parallel_cells: record.parallel_cells,
                    series_cells: record.series_cells,
                    cell_chemistry: record.cell_chemistry,
                    conn_port: record.conn_port,
                    capacity: record.capacity,
                    nominal_voltage: record.nominal_voltage,
                    max_voltage: record.max_voltage,
                    min_voltage: record.min_voltage,
                    com_protocol: record.com_protocol,
                    is_active: record.is_active,
                  });
                  setIsBatteryCategoryModalOpen(true);
                }}
              />
            ) : null}
          </>
        );
      },
    },
  ];

  const handleSearch = (e: string) => {
    if (e.length > 3) {
      dispatch(searchBy(e));
    } else if (e.length === 0) {
      dispatch(searchBy(e));
    }
  };

  return (
    <div>
      <div
        style={{
          margin: '10px 0px 10px 0px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '350px 1fr 1fr',
        }}
      >
        <Input.Search
          placeholder='Search'
          defaultValue={state.batteryCategories.search}
          style={{borderRadius: '10px'}}
          allowClear
          onSearch={(e) => handleSearch(e)}
        />
        <Select
          style={{width: '100px', marginLeft: '5px'}}
          value={
            state.batteryCategories.is_active === undefined
              ? statusFilter.ALL
              : state.batteryCategories.is_active === true
              ? statusFilter.ACTIVE
              : statusFilter.INACTIVE
          }
          options={statusFilterOptions}
          onChange={(value) => {
            if (value === statusFilter.ACTIVE) {
              dispatch(batteryCategoryStatusFilter(true));
            } else if (value === statusFilter.ALL) {
              dispatch(batteryCategoryStatusFilter(undefined));
            } else {
              dispatch(batteryCategoryStatusFilter(false));
            }
          }}
          showArrow
        />
        <div style={{width: '100%', textAlign: 'end'}}>
          {config.permissions.add_batterycategory ? (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              style={{borderRadius: '5px', marginInline: '5px'}}
              onClick={() => {
                batteryCategoryForm.resetFields();
                setIsUpdateModal(false);
                setIsBatteryCategoryModalOpen(true);
              }}
            >
              Add Category
            </Button>
          ) : null}
        </div>
      </div>
      <Table
        rowKey='id'
        columns={columns}
        pagination={
          {
            total: state.batteryCategories.totalDataCount,
            current: state.batteryCategories.page_number,
            pageSize: state.batteryCategories.page_size,
            showSizeChanger: true,
            // onChange: (pageNumber, pageSize) => {
            //   if (pageSize !== state.batteryCategories.page_size) {
            //     dispatch(pageSizeChange({pageNumber, pageSize}));
            //   } else {
            //     dispatch(pageNumberChange(pageNumber));
            //   }
            // },
          } as TablePaginationConfig
        }
        loading={state.isLoading}
        size='middle'
        dataSource={state.batteryCategories.pageData}
        scroll={{x: undefined, y: '60vh'}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(pagination, _, {order, field}: any) => {
          if (pagination.pageSize && pagination.pageSize !== state.batteryCategories.page_size) {
            dispatch(pageSizeChange(pagination.pageSize));
          }
          if (pagination.current && pagination.current !== state.batteryCategories.page_number) {
            dispatch(pageNumberChange(pagination.current));
          }
          if (field) {
            if (order === sortbyFilter.DESCEND) {
              dispatch(sortBy({ascending: false, sortBy: field}));
            } else {
              dispatch(sortBy({ascending: true, sortBy: field}));
            }
          }
        }}
      />
      <Modal
        title={`${modalData.name}`}
        open={isInfoModalOpen}
        onCancel={() => {
          setIsInfoModalOpen(false);
        }}
        footer={null}
        width={800}
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label='Name' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.name}
          </Descriptions.Item>
          <Descriptions.Item label='Status' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.is_active ? (
              <Tag color='green'>Active</Tag>
            ) : (
              <Tag color='red'>Inactive</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label='Created By' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.created_by}
          </Descriptions.Item>
          <Descriptions.Item label='Created At' labelStyle={{fontWeight: 'bolder'}}>
            {formatDate(modalData.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label='Updated By' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.updated_by}
          </Descriptions.Item>
          <Descriptions.Item label='Updated At' labelStyle={{fontWeight: 'bolder'}}>
            {formatDate(modalData.updated_at)}
          </Descriptions.Item>
          <Descriptions.Item label='Peak Current (Amps.) ' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.peak_current}
          </Descriptions.Item>
          <Descriptions.Item label='Capacity (Ah) ' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.capacity}
          </Descriptions.Item>
          <Descriptions.Item label='Parallel Cells ' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.parallel_cells}
          </Descriptions.Item>
          <Descriptions.Item label='Series Cells' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.series_cells}
          </Descriptions.Item>
          <Descriptions.Item label='Conn. Port ' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.conn_port}
          </Descriptions.Item>
          <Descriptions.Item label='Cell Chemistry ' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.cell_chemistry}
          </Descriptions.Item>
          <Descriptions.Item label='Nominal Voltage (Volt)' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.nominal_voltage}
          </Descriptions.Item>
          <Descriptions.Item label='Max. Voltage (Volt)' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.max_voltage}
          </Descriptions.Item>
          <Descriptions.Item label='Min. Voltage (Volt)' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.min_voltage}
          </Descriptions.Item>
          <Descriptions.Item label='Connection Protocols' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.com_protocol?.map((x) => {
              return <>{x.toUpperCase()}, </>;
            })}
          </Descriptions.Item>
        </Descriptions>
      </Modal>
      <Modal
        title={isUpdateModal ? 'Update Battery Caegory' : 'Add Battery Category'}
        open={isBatteryCategoryModalOpen}
        maskClosable={false}
        onCancel={() => {
          // batteryCategoryForm.resetFields();
          setIsBatteryCategoryModalOpen(false);
        }}
        okText={isUpdateModal ? 'Update Category' : 'Add Category'}
        width={600}
        confirmLoading={state.isLoading}
        onOk={() => {
          batteryCategoryForm
            .validateFields()
            .then((values) => {
              //batteryCategoryForm.resetFields();
              {
                isUpdateModal
                  ? handleUpdateBatteryCategory(values)
                  : handleAddBatteryCategory(values);
              }
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Row>
          <Col span={24}>
            <Form name='updateBatteryCategory' form={batteryCategoryForm} layout='vertical'>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='name'
                    label='Category Name'
                    rules={[
                      {required: true, message: 'Please fill your full Name '},
                      {min: 4, message: 'Minimum length is 4 characters'},
                    ]}
                  >
                    <Input autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='nominal_voltage'
                    label={
                      <>
                        Nominal Volatge<span style={{color: '#1890FF'}}>(Volt)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Please fill Nominal Voltage '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='peak_current'
                    label={
                      <>
                        Peak Current<span style={{color: '#1890FF'}}>(Amp.)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Please fill peak current '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='capacity'
                    label={
                      <>
                        Capacity <span style={{color: '#1890FF'}}>(Ah)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Please fill Capacity '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='series_cells'
                    label='Series Cells'
                    rules={[{required: true, message: 'Please fill series cells '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='parallel_cells'
                    label='Parallel Cells'
                    rules={[{required: true, message: 'Please fill Parallel Cells'}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='min_voltage'
                    label={
                      <>
                        Min. Volatge<span style={{color: '#1890FF'}}>(Volt)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Please fill Min. Voltage '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='max_voltage'
                    label={
                      <>
                        Max. Volatge<span style={{color: '#1890FF'}}>(Volt)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Please fill Max. Voltage '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='cell_chemistry'
                    label='Cell Chemistry'
                    rules={[{required: true, message: 'Please fill Cell Chemistry '}]}
                  >
                    <Select placeholder='Please Select' filterOption={false}>
                      {config.config.cell_chemistry_options?.map((x) => (
                        <Option key={x.value}>{x.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='conn_port'
                    label='Conection Port'
                    rules={[{required: true, message: 'Please fill Connection Port '}]}
                  >
                    <Select placeholder='Please Select' filterOption={false}>
                      {config.config.connection_type_options?.map((x) => (
                        <Option key={x.value}>{x.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={14}>
                  <Form.Item
                    label='Communication Protocols'
                    name='com_protocol'
                    rules={[{required: true, message: 'Select atleast 1 option'}]}
                  >
                    <Select mode='multiple' allowClear placeholder='Please select' listHeight={200}>
                      {config.config.connection_protocol_options.map((x) => (
                        <Option key={x.value}>{x.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                {isUpdateModal && (
                  <Col offset={2}>
                    <Form.Item name='is_active' label='Status' valuePropName='checked'>
                      <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Form>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default BatteryCategory;
